import React from 'react'
import { ComponenteBaseEnlace, ImagenEnlace, PrecioEnlace } from './ComponenteBase'

const DiseñoNeon = ({ 
  enlaces = [], 
  estilosMiniatura = false, 
  esPreview = false,
  configuracion = {}
}) => {
  // Configuración por defecto para el diseño Neon
  const config = {
    colorPrimario: 'cyan', // cyan | pink | purple | green | yellow | red
    intensidad: 'media', // baja | media | alta
    animacion: 'pulse', // none | pulse | glow | flicker
    fondo: 'oscuro', // oscuro | transparente
    ...configuracion
  }

  // Obtener colores según configuración
  const obtenerColores = () => {
    const colores = {
      cyan: {
        borde: 'border-cyan-400',
        texto: 'text-cyan-400',
        sombra: 'shadow-cyan-400/50',
        fondo: 'bg-cyan-400/10',
        hover: 'hover:border-cyan-300 hover:text-cyan-300 hover:shadow-cyan-300/60'
      },
      pink: {
        borde: 'border-pink-400',
        texto: 'text-pink-400',
        sombra: 'shadow-pink-400/50',
        fondo: 'bg-pink-400/10',
        hover: 'hover:border-pink-300 hover:text-pink-300 hover:shadow-pink-300/60'
      },
      purple: {
        borde: 'border-purple-400',
        texto: 'text-purple-400',
        sombra: 'shadow-purple-400/50',
        fondo: 'bg-purple-400/10',
        hover: 'hover:border-purple-300 hover:text-purple-300 hover:shadow-purple-300/60'
      },
      green: {
        borde: 'border-green-400',
        texto: 'text-green-400',
        sombra: 'shadow-green-400/50',
        fondo: 'bg-green-400/10',
        hover: 'hover:border-green-300 hover:text-green-300 hover:shadow-green-300/60'
      },
      yellow: {
        borde: 'border-yellow-400',
        texto: 'text-yellow-400',
        sombra: 'shadow-yellow-400/50',
        fondo: 'bg-yellow-400/10',
        hover: 'hover:border-yellow-300 hover:text-yellow-300 hover:shadow-yellow-300/60'
      },
      red: {
        borde: 'border-red-400',
        texto: 'text-red-400',
        sombra: 'shadow-red-400/50',
        fondo: 'bg-red-400/10',
        hover: 'hover:border-red-300 hover:text-red-300 hover:shadow-red-300/60'
      }
    }
    
    return colores[config.colorPrimario] || colores.cyan
  }

  // Obtener intensidad de sombra
  const obtenerIntensidadSombra = () => {
    switch (config.intensidad) {
      case 'baja':
        return 'shadow-sm'
      case 'alta':
        return 'shadow-2xl'
      default:
        return 'shadow-lg'
    }
  }

  // Obtener clases de animación
  const obtenerClasesAnimacion = () => {
    let clases = ['transition-all duration-300']
    
    switch (config.animacion) {
      case 'pulse':
        clases.push('animate-pulse')
        break
      case 'glow':
        clases.push('hover:animate-ping')
        break
      case 'flicker':
        clases.push('animate-bounce')
        break
      default:
        break
    }
    
    return clases.join(' ')
  }

  // Clases base del elemento neón
  const obtenerClasesNeon = () => {
    const colores = obtenerColores()
    let clases = [
      'border-2 rounded-lg p-4 backdrop-blur-sm',
      colores.borde,
      colores.texto,
      colores.sombra,
      colores.hover,
      obtenerIntensidadSombra(),
      obtenerClasesAnimacion()
    ]
    
    if (config.fondo === 'oscuro') {
      clases.push('bg-black/80')
    } else {
      clases.push(colores.fondo)
    }
    
    return clases.join(' ')
  }

  // Estilos inline para efectos neón más intensos
  const obtenerEstilosNeon = () => {
    const colores = {
      cyan: '#00ffff',
      pink: '#ff00ff',
      purple: '#8a2be2',
      green: '#00ff00',
      yellow: '#ffff00',
      red: '#ff0000'
    }
    
    const color = colores[config.colorPrimario] || colores.cyan
    let intensidad = 10
    
    switch (config.intensidad) {
      case 'baja':
        intensidad = 5
        break
      case 'alta':
        intensidad = 20
        break
      default:
        intensidad = 10
    }
    
    return {
      boxShadow: `0 0 ${intensidad}px ${color}, 0 0 ${intensidad * 2}px ${color}, 0 0 ${intensidad * 3}px ${color}`,
      textShadow: `0 0 ${intensidad / 2}px ${color}`
    }
  }

  // Ajustar para estilos miniatura
  if (estilosMiniatura) {
    return (
      <div className="space-y-1">
        {enlaces.map((enlace, index) => (
          <ComponenteBaseEnlace
            key={enlace.id || index}
            enlace={enlace}
            estilosMiniatura={estilosMiniatura}
            esPreview={esPreview}
            className="enlace-miniatura"
          >
            <div 
              className={`${obtenerClasesNeon()} !p-2 mb-1`}
              style={{
                ...obtenerEstilosNeon(),
                boxShadow: `0 0 5px ${obtenerColores().sombra.split('-')[1]}`
              }}
            >
              <div className="space-y-1">
                {/* Título arriba */}
                <h3 className="font-bold text-xs leading-tight truncate">
                  {enlace.titulo}
                </h3>
                
                {/* Imagen y precio */}
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0">
                    <ImagenEnlace 
                      enlace={enlace} 
                      estilosMiniatura={estilosMiniatura}
                      mostrarIcono={false}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    {/* Precio para productos */}
                    {enlace.tipo === 'producto' && (
                      <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                    )}
                  </div>
                </div>
                
                {/* Descripción abajo */}
                {enlace.descripcion && (
                  <p className="text-xs leading-tight opacity-90 font-medium">
                    {enlace.descripcion}
                  </p>
                )}
                
                {/* Precio para enlaces que no son productos */}
                {enlace.tipo !== 'producto' && (
                  <div>
                    <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                  </div>
                )}
              </div>
            </div>
          </ComponenteBaseEnlace>
        ))}
      </div>
    )
  }

  // Diseño normal para desktop
  return (
    <div className="space-y-6">
      {enlaces.map((enlace, index) => (
        <ComponenteBaseEnlace
          key={enlace.id || index}
          enlace={enlace}
          estilosMiniatura={estilosMiniatura}
          esPreview={esPreview}
          className="group"
        >
          <div 
            className={`${obtenerClasesNeon()} group-hover:scale-105`}
            style={obtenerEstilosNeon()}
          >
            <div className="space-y-4">
              {/* Título arriba */}
              <h3 className="font-bold text-xl tracking-wide">
                {enlace.titulo}
              </h3>
              
              {/* Imagen y precio */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <ImagenEnlace 
                    enlace={enlace} 
                    estilosMiniatura={estilosMiniatura}
                    mostrarIcono={false}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  {/* Precio para productos */}
                  {enlace.tipo === 'producto' && (
                    <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                  )}
                </div>
              </div>
              
              {/* Descripción abajo */}
              {enlace.descripcion && (
                <p className="text-sm opacity-90 font-medium">
                  {enlace.descripcion}
                </p>
              )}
              
              {/* Precio para enlaces que no son productos */}
              {enlace.tipo !== 'producto' && (
                <div>
                  <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                </div>
              )}
            </div>
          </div>
        </ComponenteBaseEnlace>
      ))}
    </div>
  )
}

export default DiseñoNeon 